import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { GridReadyEvent, GridApi, ICellRendererParams } from 'ag-grid-community';
import * as XLSX from 'xlsx';
import CommonButton from '../../../components/common-components/button/CommonButton';
import styles from './HospitalFiles.module.css';
import toolbarStyles from '../../../styles/ToolbarButtons.module.css';
import { Pencil, Trash2, FileSpreadsheet, RotateCw } from 'lucide-react';
import NewRowManuel from './parts/new-data/new-row/manuel/NewRowManuel';
import NewRowQrCode from './parts/new-data/new-row/qr-code/NewRowQrCode';
import EditRowManually from './parts/edit-data/EditRowManually';
import { useStore } from '../../../hooks/useStore';
import { observer } from 'mobx-react-lite';
import SwitchHospital from './parts/switch-data/SwitchHospital';
import { IHospitalRecordResult } from '../../../models/hospital/HospitalModel';
import DeleteConfirmationDialog from './parts/delete/DeleteConfirmationDialog';
import NewRowFromKn from './parts/new-data/new-row/from-kn/NewRowFromKn';
import BaseAgGrid, { type GridFetchParams } from '../../../components/common-components/grid/BaseAgGrid';
import { useHospitalFilesColumnDefs, useHospitalFilesDefaultColDef } from './HospitalFiles.grid';

const ActionCellRenderer = (props: ICellRendererParams<IHospitalRecordResult>) => {
  const { popupStore, hospitalFilesStore } = useStore();

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      hospitalFilesStore.setSelectedRecord(props.data);
      popupStore.setIsEditManuelRowOpen(true);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data?.id) {
      hospitalFilesStore.setSelectedRecordToDelete(props.data.id);
      popupStore.setIsDeleteConfirmationOpen(true);
    }
  };

  return (
    <div className={styles.actionCell}>
      <button onClick={handleEdit} className={styles.editButton} title="Düzenle">
        <Pencil size={20} />
      </button>
      <button onClick={handleDelete} className={styles.deleteButton} title="Sil">
        <Trash2 size={20} />
      </button>
    </div>
  );
};

const HospitalFiles = () => {
  const [gridApi, setGridApi] = useState<GridApi<IHospitalRecordResult> | null>(null);
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const { popupStore, hospitalFilesStore } = useStore();
  const lastSelectedHospitalIdRef = useRef<number | null>(null);

  const colorItems = useMemo<{ key: string; label: string; dot: string; value: number }[]>(() => {
    const map = new Map<string, number>();
    (hospitalFilesStore.tagColors || []).forEach(c => {
      if (c?.color) map.set(c.color.toLowerCase(), c.sequence ?? 0);
    });
    return [
      { key: 'red', label: 'kırmızı', dot: '#ef4444' },
      { key: 'yellow', label: 'sarı', dot: '#eab308' },
      { key: 'green', label: 'yeşil', dot: '#22c55e' },
    ].map(x => ({ ...x, value: map.get(x.key) ?? 0 }));
  }, [hospitalFilesStore.tagColors]);

  useEffect(() => {
    if (!hospitalFilesStore.selectedHospital) {
      popupStore.setIsSwitchHospitalOpen(true);
    }
    // start SignalR live updates when page is open
    hospitalFilesStore.startLiveTagColors();
    return () => {
      hospitalFilesStore.stopLiveTagColors();
    };
  }, []);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    hospitalFilesStore.getTagColors();
  }, [hospitalFilesStore.selectedTag?.id]);

  const fetchRows = useCallback(
    async (params: GridFetchParams) => {
      if (!hospitalFilesStore.selectedHospital) {
        return { rows: [], lastRow: 0 };
      }
      const result = await hospitalFilesStore.listHospitalRecord(params);
      return { rows: result.rows, lastRow: result.lastRow };
    },
    [hospitalFilesStore, hospitalFilesStore.selectedHospital]
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent<IHospitalRecordResult>) => {
      setGridApi(params.api);
      hospitalFilesStore.setGridApi(params.api);
    },
    [hospitalFilesStore]
  );

  useEffect(() => {
    const selectedId = hospitalFilesStore.selectedHospital?.id ?? null;
    if (!gridApi) {
      lastSelectedHospitalIdRef.current = selectedId;
      return;
    }
    if (lastSelectedHospitalIdRef.current === selectedId) return;
    lastSelectedHospitalIdRef.current = selectedId;
    gridApi.refreshInfiniteCache();
    gridApi.refreshServerSide();
  }, [gridApi, hospitalFilesStore.selectedHospital?.id]);

  const exportToExcel = useCallback(async () => {
    if (!gridApi) return;

    gridApi.showLoadingOverlay();

    try {
      const result = await hospitalFilesStore.listHospitalRecord({
        startRow: 0,
        endRow: 1000000,
        filterModel: gridApi.getFilterModel(),
        sortModel: null,
      });

      if (!result?.rows.length) {
        gridApi.hideOverlay();
        return;
      }

      const exportData = result.rows.map(item => ({
        ID: item.id,
        Hastane: item.hospitalName,
        'Cihaz Tanımı': item.biomedicalDeviceDescription,
        Marka: item.brand,
        Model: item.model,
        'Seri No': item.serialNumber,
        'Künye No': item.identificationNumber,
        Lokasyon: item.location,
        'Etiket No': item.tagNumberWithColor,
        'Eklenme Tarihi': new Date(item.addDate).toLocaleDateString('tr-TR'),
        'Zimmetli Kişi': item.responsiblePerson,
        Notlar: item.note,
        'Ekleyen Kişi': item.createdBy,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Hastane Cihazları');
      const hospitalName = hospitalFilesStore.selectedHospital?.hospitalName || 'Hastane';
      const sanitizedHospitalName = hospitalName
        .replace(/[ıİ]/g, 'I')
        .replace(/[ğĞ]/g, 'G')
        .replace(/[üÜ]/g, 'U')
        .replace(/[şŞ]/g, 'S')
        .replace(/[öÖ]/g, 'O')
        .replace(/[çÇ]/g, 'C')
        .replace(/[^a-zA-Z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');

      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = sanitizedHospitalName
        ? `${sanitizedHospitalName}_Hastane_Cihazlari_${dateStr}.xlsx`
        : `Hastane_Cihazlari_${dateStr}.xlsx`;

      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Excel export error:', error);
    } finally {
      gridApi.hideOverlay();
    }
  }, [gridApi, hospitalFilesStore]);

  const columnDefs = useHospitalFilesColumnDefs(ActionCellRenderer);
  const defaultColDef = useHospitalFilesDefaultColDef();
  const isMobile = viewportWidth < 768;
  const isTablet = viewportWidth >= 768 && viewportWidth < 1024;
  const gridRowHeight = isMobile ? 40 : isTablet ? 44 : 48;
  const gridHeaderHeight = isMobile ? 40 : isTablet ? 44 : 48;
  const pageSize = isMobile ? 10 : 20;

  const handleReadFromQr = () => {
    popupStore.setIsNewQrCodeOpen(true);
  };

  const handleNewManuelRow = () => {
    popupStore.setIsNewManuelRowOpen(true);
  };

  const handleSwitchHospital = () => {
    popupStore.setIsSwitchHospitalOpen(true);
  };
  const handleNewKnRow = () => {
    popupStore.setIsNewKnRowOpen(true);
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.mainTitle}>Hastane Dosyaları</h2>
          <div className={styles.infoRow}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Seçilen Hastane:</span>
              <span className={styles.infoValue}>
                {hospitalFilesStore.selectedHospital?.hospitalName}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Seçilen Etiket No:</span>
              <span className={styles.infoValue}>{hospitalFilesStore.selectedTag?.prefix}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Güncel Etiket No Değerleri:</span>
              <div className={styles.tagColorList}>
                {colorItems.map(item => (
                  <span key={item.key} className={styles.tagColorItem}>
                    <span className={styles.colorDot} style={{ backgroundColor: item.dot }} />
                    <span className={styles.tagColorLabel}>{item.label}:</span>
                    <span className={styles.tagColorValue}>{item.value}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.gridContainer}>
          <BaseAgGrid<IHospitalRecordResult>
            fetchRows={fetchRows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            toolbarLeft={({ refresh }) => (
              <>
                <CommonButton
                  type="outline"
                  onClick={handleSwitchHospital}
                  className={styles.toolbarBtnSwitch}
                >
                  Hastane Değiştir
                </CommonButton>
                <CommonButton
                  type="primary"
                  onClick={handleNewManuelRow}
                  className={styles.toolbarBtnManual}
                >
                  Yeni Veri Girişi
                </CommonButton>
                <CommonButton
                  type="outline"
                  onClick={handleNewKnRow}
                  className={styles.toolbarBtnKn}
                >
                  Künye Numarası İle Veri Girişi
                </CommonButton>
                <CommonButton
                  type="tertiary"
                  onClick={handleReadFromQr}
                  className={styles.toolbarBtnQr}
                >
                  QR'dan Oku
                </CommonButton>
                <CommonButton
                  type="tertiary"
                  onClick={exportToExcel}
                  leftIcon={<FileSpreadsheet size={18} />}
                  className={toolbarStyles.toolbarBtnExcel}
                >
                  Excel'e Aktar
                </CommonButton>
                <CommonButton
                  type="refresh"
                  onClick={refresh}
                  leftIcon={<RotateCw size={18} />}
                  className={toolbarStyles.toolbarBtnRefresh}
                >
                  Yenile
                </CommonButton>
              </>
            )}
            paginationPageSize={pageSize}
            rowHeight={gridRowHeight}
            headerHeight={gridHeaderHeight}
            getRowStyle={params => {
              const color = params.data?.color;
              if (color === 'red') {
                return { backgroundColor: '#ffebee' };
              } else if (color === 'yellow') {
                return { backgroundColor: '#fff8e1' };
              } else if (color === 'green') {
                return { backgroundColor: '#e8f5e8' };
              }
              return undefined;
            }}
          />
        </div>
      </div>
      {popupStore.isNewManuelRowOpen && <NewRowManuel />}
      {popupStore.isNewQrCodeRowOpen && <NewRowQrCode />}
      {popupStore.isEditManuelRowOpen && <EditRowManually />}
      {popupStore.isSwitchHospitalOpen && <SwitchHospital />}
      {popupStore.isNewKnRowOpen && <NewRowFromKn />}
      <DeleteConfirmationDialog
        isOpen={popupStore.isDeleteConfirmationOpen}
        onClose={() => popupStore.setIsDeleteConfirmationOpen(false)}
        onConfirm={async () => {
          if (hospitalFilesStore.selectedRecordToDelete) {
            await hospitalFilesStore.deleteRecord(hospitalFilesStore.selectedRecordToDelete);
            hospitalFilesStore.setSelectedRecordToDelete(null);
          }
          popupStore.setIsDeleteConfirmationOpen(false);
        }}
        title="Kayıt Silme Onayı"
      />
    </>
  );
};

export default observer(HospitalFiles);
